import {
  BOOKING_APPROVAL_LABEL,
  BOOKING_PAYMENT_LABEL,
  BOOKING_STYLE_LABEL,
  BOOKING_STYLES,
  createProductSchema,
  generateVariantsSchema,
  quotaText,
  updateProductSchema,
  type BookingApproval,
  type BookingPayment,
  type BookingQuotaPeriod,
  type BookingStyle,
} from '@alicerce/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Dialog,
  Field,
  Input,
  MoneyInput,
  PageHeader,
  PageLoader,
  Select,
  Table,
  Td,
  Textarea,
  TextField,
  Th,
  Tr,
} from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { FilePicker, useUpload } from '@/components/upload';
import { errorMessage } from '@/lib/api';
import { useZodForm } from '@/lib/form';
import { useApi, useCurrent } from '@/lib/session';
import { flattenCategories, STATUS_LABEL, type CategoryNode, type Product, type Variant } from './types';

const num = (v: string | number | null | undefined) => (v === null || v === undefined ? null : Number(v));

function useCategories() {
  const api = useApi();
  return useQuery({ queryKey: ['categories'], queryFn: () => api.get<CategoryNode[]>('/v1/core/categories') });
}

const SERVICE_KEYS = ['subscriptionInterval', 'bookingMode', 'bookingDurationMinutes', 'bookingQuota', 'bookingQuotaPeriod', 'bookingStyles'] as const;

type BookingMode = 'SLOT' | 'DAY' | null;
type Interval = 'MONTHLY' | 'WEEKLY' | null;

/** Campos de serviço que dependem de assinatura e agenda. */
export type ServiceValues = {
  subscriptionInterval?: Interval;
  bookingMode?: BookingMode;
  bookingDurationMinutes?: number | null;
  bookingQuota?: number | null;
  bookingQuotaPeriod?: BookingQuotaPeriod | null;
  bookingStyles?: BookingStyle[] | null;
  bookingPayment?: BookingPayment;
  bookingApproval?: BookingApproval;
};

/**
 * Serviço vendido avulso ou por assinatura (mensal ou semanal) e, com a Agenda online contratada,
 * marcado pela agenda. Na assinatura com agenda o dono define quantos atendimentos cabem em cada
 * semana ou mês e se o cliente pode escolher horário fixo, marcar cada vez, ou os dois.
 */
function ServiceFields({
  values,
  errors,
  agenda,
  checkout,
  onChange,
}: {
  values: ServiceValues;
  errors: Record<string, string | undefined>;
  agenda: boolean;
  /** Pagamentos online contratado: dá para cobrar o Pix na hora de agendar. */
  checkout: boolean;
  onChange: (patch: ServiceValues) => void;
}) {
  const interval = values.subscriptionInterval ?? null;
  const mode = values.bookingMode ?? null;
  const styles = values.bookingStyles ?? [];
  const setInterval = (next: Interval) => {
    const patch: ServiceValues = { subscriptionInterval: next };
    if (!next) Object.assign(patch, { bookingQuota: null, bookingQuotaPeriod: null, bookingStyles: null });
    else if (mode) Object.assign(patch, subscriptionBooking(next, values), { bookingPayment: 'ON_SITE' });
    onChange(patch);
  };
  const setMode = (next: BookingMode) => {
    const patch: ServiceValues = { bookingMode: next, bookingDurationMinutes: next === 'SLOT' ? (values.bookingDurationMinutes ?? 60) : null };
    if (!next) Object.assign(patch, { bookingQuota: null, bookingQuotaPeriod: null, bookingStyles: null });
    else if (interval) Object.assign(patch, subscriptionBooking(interval, values));
    onChange(patch);
  };
  const toggleStyle = (style: BookingStyle, on: boolean) => {
    const next = on ? [...styles.filter((s) => s !== style), style] : styles.filter((s) => s !== style);
    if (next.length > 0) onChange({ bookingStyles: BOOKING_STYLES.filter((s) => next.includes(s)) });
  };
  return (
    <div className="space-y-4">
      <div>
        <Checkbox label="Vender por assinatura" checked={!!interval} onChange={(e) => setInterval(e.target.checked ? 'MONTHLY' : null)} />
        <p className="mt-1 text-xs text-muted">O preço vira o valor de cada cobrança, feita automaticamente. Acompanhe em Assinaturas.</p>
      </div>
      {interval && (
        <Field label="Cobrança">
          {(a) => (
            <Select {...a} value={interval} onChange={(e) => setInterval(e.target.value as Interval)}>
              <option value="MONTHLY">Mensal (todo mês)</option>
              <option value="WEEKLY">Semanal (toda semana)</option>
            </Select>
          )}
        </Field>
      )}
      {(agenda || mode) && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Agendamento online" hint="O cliente escolhe dia e horário livres na sua agenda.">
            {(a) => (
              <Select {...a} value={mode ?? ''} onChange={(e) => setMode((e.target.value || null) as BookingMode)}>
                <option value="">Não usa a agenda</option>
                <option value="SLOT">Por horário (vários clientes no dia)</option>
                <option value="DAY">O dia todo (um cliente por dia)</option>
              </Select>
            )}
          </Field>
          {mode === 'SLOT' && (
            <TextField
              label="Duração do atendimento (minutos)"
              type="number"
              min={5}
              max={720}
              value={values.bookingDurationMinutes ?? ''}
              error={errors.bookingDurationMinutes}
              onChange={(e) => onChange({ bookingDurationMinutes: e.target.value ? Number(e.target.value) : null })}
            />
          )}
        </div>
      )}
      {mode && (
        <div className="grid gap-4 sm:grid-cols-2">
          {!interval && (
            <Field
              label="Quando o cliente paga"
              hint={
                values.bookingPayment === 'ONLINE'
                  ? 'O cliente paga com Pix ao agendar. O horário só fica marcado depois do pagamento.'
                  : checkout
                    ? 'Escolha "Na hora de agendar" para só marcar horários já pagos.'
                    : 'Para cobrar na hora de agendar, contrate Pagamentos online.'
              }
            >
              {(a) => (
                <Select {...a} value={values.bookingPayment ?? 'ON_SITE'} onChange={(e) => onChange({ bookingPayment: e.target.value as BookingPayment })}>
                  <option value="ON_SITE">{BOOKING_PAYMENT_LABEL.ON_SITE}</option>
                  <option value="ONLINE" disabled={!checkout && values.bookingPayment !== 'ONLINE'}>
                    {BOOKING_PAYMENT_LABEL.ONLINE}
                  </option>
                </Select>
              )}
            </Field>
          )}
          <Field
            label="Confirmação do agendamento"
            hint={values.bookingApproval === 'MANUAL' ? 'O horário fica guardado até você confirmar ou recusar na Agenda.' : 'O agendamento feito na loja já sai confirmado.'}
          >
            {(a) => (
              <Select {...a} value={values.bookingApproval ?? 'AUTO'} onChange={(e) => onChange({ bookingApproval: e.target.value as BookingApproval })}>
                <option value="AUTO">{BOOKING_APPROVAL_LABEL.AUTO}</option>
                <option value="MANUAL">{BOOKING_APPROVAL_LABEL.MANUAL}</option>
              </Select>
            )}
          </Field>
        </div>
      )}
      {interval && mode && (
        <div className="space-y-3 rounded-md border border-line p-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Atendimentos por período"
              type="number"
              min={1}
              max={31}
              value={values.bookingQuota ?? ''}
              error={errors.bookingQuota}
              onChange={(e) => onChange({ bookingQuota: e.target.value ? Number(e.target.value) : null })}
            />
            <Field label="Período" error={errors.bookingQuotaPeriod}>
              {(a) => (
                <Select {...a} value={values.bookingQuotaPeriod ?? 'MONTH'} onChange={(e) => onChange({ bookingQuotaPeriod: e.target.value as BookingQuotaPeriod })}>
                  <option value="WEEK">Por semana</option>
                  <option value="MONTH" disabled={interval === 'WEEKLY'}>
                    Por mês
                  </option>
                </Select>
              )}
            </Field>
          </div>
          {values.bookingQuota && values.bookingQuotaPeriod && (
            <p className="text-xs text-muted">Quem assinar pode marcar {quotaText(values.bookingQuota, values.bookingQuotaPeriod)}.</p>
          )}
          <fieldset className="space-y-1">
            <legend className="mb-1 text-sm font-medium">Na compra, o cliente pode escolher</legend>
            {BOOKING_STYLES.map((s) => (
              <Checkbox key={s} label={BOOKING_STYLE_LABEL[s]} checked={styles.includes(s)} onChange={(e) => toggleStyle(s, e.target.checked)} />
            ))}
          </fieldset>
        </div>
      )}
    </div>
  );
}

/** Valores iniciais da cota quando o serviço passa a ser assinatura com agenda. */
function subscriptionBooking(interval: 'MONTHLY' | 'WEEKLY', v: ServiceValues): ServiceValues {
  const period = interval === 'WEEKLY' ? 'WEEK' : (v.bookingQuotaPeriod ?? 'MONTH');
  return {
    bookingQuota: v.bookingQuota ?? (period === 'WEEK' ? 1 : 4),
    bookingQuotaPeriod: period,
    bookingStyles: v.bookingStyles?.length ? v.bookingStyles : ['FIXED', 'FLEXIBLE'],
  };
}

/** Novo produto: o essencial. Variantes e fotos vêm na tela do produto, logo depois. */
export function NewProductPage() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const categories = useCategories();
  const { isModuleActive } = useCurrent();
  const form = useZodForm(createProductSchema, {
    type: 'PRODUCT' as 'PRODUCT' | 'SERVICE',
    status: 'ACTIVE' as Product['status'],
    name: '',
    description: '',
    categoryId: null as string | null,
    priceCents: null as number | null,
    costCents: null as number | null,
    sku: '',
    subscriptionInterval: null as Interval,
    bookingMode: null as BookingMode,
    bookingDurationMinutes: null as number | null,
    bookingQuota: null as number | null,
    bookingQuotaPeriod: null as BookingQuotaPeriod | null,
    bookingStyles: null as BookingStyle[] | null,
    bookingPayment: 'ON_SITE' as BookingPayment,
    bookingApproval: 'AUTO' as BookingApproval,
  });
  const onSubmit = form.submit(async (data) => {
    const p = await api.post<Product>('/v1/core/products', { ...data, sku: data.sku || undefined, priceCents: data.priceCents ?? 0 });
    await queryClient.invalidateQueries({ queryKey: ['products'] });
    navigate(`/produtos/${p.id}?novo=1`, { replace: true });
  });
  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title="Novo produto" description="Depois de salvar, você adiciona fotos e variantes (cor, tamanho)." />
      <Card>
        <CardBody>
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            {form.formError && <Alert tone="danger">{form.formError}</Alert>}
            <TextField label="Nome" value={form.values.name} error={form.errors.name} onChange={(e) => form.set('name', e.target.value)} autoFocus />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Preço de venda" error={form.errors.priceCents}>
                {(a) => <MoneyInput {...a} value={form.values.priceCents} onChange={(v) => form.set('priceCents', v)} />}
              </Field>
              <Field label="Custo (opcional)" hint="Usado no lucro e na calculadora de preço." error={form.errors.costCents}>
                {(a) => <MoneyInput {...a} value={form.values.costCents} onChange={(v) => form.set('costCents', v)} />}
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Tipo">
                {(a) => (
                  <Select
                    {...a}
                    value={form.values.type}
                    onChange={(e) => {
                      form.set('type', e.target.value as 'PRODUCT' | 'SERVICE');
                      if (e.target.value !== 'SERVICE') {
                        for (const k of SERVICE_KEYS) form.set(k, null);
                      }
                    }}
                  >
                    <option value="PRODUCT">Produto físico</option>
                    <option value="SERVICE">Serviço</option>
                  </Select>
                )}
              </Field>
              <Field label="Categoria">
                {(a) => (
                  <Select {...a} value={form.values.categoryId ?? ''} onChange={(e) => form.set('categoryId', e.target.value || null)}>
                    <option value="">Sem categoria</option>
                    {flattenCategories(categories.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            {form.values.type === 'SERVICE' && (
              <ServiceFields
                values={form.values}
                errors={form.errors}
                agenda={isModuleActive('agenda')}
                checkout={isModuleActive('checkout')}
                onChange={(patch) => {
                  for (const [k, v] of Object.entries(patch)) form.set(k as keyof ServiceValues, v as never);
                }}
              />
            )}
            <TextField label="SKU (opcional)" hint="Se deixar vazio, geramos um código." value={form.values.sku} error={form.errors.sku} onChange={(e) => form.set('sku', e.target.value)} />
            <Field label="Descrição" error={form.errors.description}>
              {(a) => <Textarea {...a} value={form.values.description ?? ''} onChange={(e) => form.set('description', e.target.value)} />}
            </Field>
            <div className="flex justify-end gap-2">
              <Link to="/produtos" className="inline-flex h-10 items-center px-4 text-sm">
                Cancelar
              </Link>
              <Button type="submit" loading={form.submitting}>
                Salvar e continuar
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

type VariantDraft = {
  id: string;
  sku: string;
  name: string;
  options: Record<string, string>;
  priceCents: number | null;
  compareAtCents: number | null;
  costCents: number | null;
  weightGrams: number | null;
  heightCm: number | null;
  widthCm: number | null;
  lengthCm: number | null;
  trackInventory: boolean;
};

const toDraft = (v: Variant): VariantDraft => ({
  id: v.id,
  sku: v.sku,
  name: v.name,
  options: v.options,
  priceCents: num(v.priceCents),
  compareAtCents: num(v.compareAtCents),
  costCents: num(v.costCents),
  weightGrams: v.weightGrams,
  heightCm: v.heightCm,
  widthCm: v.widthCm,
  lengthCm: v.lengthCm,
  trackInventory: v.trackInventory,
});

export function EditProductPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const q = useQuery({ queryKey: ['product', id], queryFn: () => api.get<Product>(`/v1/core/products/${id}`) });
  if (q.isPending) return <PageLoader />;
  if (q.isError) return <Alert tone="danger">{errorMessage(q.error)}</Alert>;
  return <ProductForm key={q.data.updatedAt} product={q.data} />;
}

function ProductForm({ product }: { product: Product }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, isModuleActive } = useCurrent();
  const editable = can('catalog:write');
  const categories = useCategories();
  const fiscal = isModuleActive('fiscal');
  const profiles = useQuery({
    queryKey: ['fiscal', 'tax-profiles'],
    queryFn: () => api.get<{ id: string; name: string }[]>('/v1/fiscal/tax-profiles'),
    enabled: fiscal && can('fiscal:read'),
  });
  const [saved, setSaved] = useState(false);
  const form = useZodForm(updateProductSchema, {
    name: product.name,
    status: product.status,
    type: product.type,
    slug: product.slug,
    description: product.description ?? '',
    categoryId: product.categoryId,
    featured: product.featured,
    ncm: product.ncm,
    cest: product.cest,
    origin: product.origin,
    unit: product.unit,
    serviceCode: product.serviceCode,
    taxProfileId: product.taxProfileId,
    subscriptionInterval: product.subscriptionInterval ?? null,
    bookingMode: (product.bookingMode ?? null) as BookingMode,
    bookingDurationMinutes: product.bookingDurationMinutes ?? null,
    bookingQuota: product.bookingQuota ?? null,
    bookingQuotaPeriod: product.bookingQuotaPeriod ?? null,
    bookingStyles: product.bookingStyles?.length ? product.bookingStyles : null,
    bookingPayment: product.bookingPayment ?? 'ON_SITE',
    bookingApproval: product.bookingApproval ?? 'AUTO',
    seo: { title: product.seo?.title ?? '', description: product.seo?.description ?? '' },
    variants: product.variants.map(toDraft),
  });
  const variants = form.values.variants as VariantDraft[];
  const setVariant = (i: number, patch: Partial<VariantDraft>) =>
    form.set(
      'variants',
      variants.map((v, j) => (j === i ? { ...v, ...patch } : v)),
    );

  const onSubmit = form.submit(async (data) => {
    setSaved(false);
    await api.patch(`/v1/core/products/${product.id}`, data);
    await queryClient.invalidateQueries({ queryKey: ['products'] });
    await queryClient.invalidateQueries({ queryKey: ['product', product.id] });
    setSaved(true);
  });

  const archive = useMutation({
    mutationFn: () => api.delete(`/v1/core/products/${product.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['products'] });
      navigate('/produtos');
    },
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <PageHeader
        title={product.name}
        description={
          <span className="flex items-center gap-2">
            <Badge tone={product.status === 'ACTIVE' ? 'success' : 'neutral'}>{STATUS_LABEL[product.status]}</Badge>
            <Link to="/produtos" className="text-brand-700 hover:underline">
              Voltar para produtos
            </Link>
          </span>
        }
        actions={
          editable && (
            <>
              {product.status !== 'ARCHIVED' && (
                <Button variant="ghost" onClick={() => archive.mutate()} loading={archive.isPending}>
                  Arquivar
                </Button>
              )}
              <Button type="submit" loading={form.submitting}>
                Salvar
              </Button>
            </>
          )
        }
      />
      {form.formError && (
        <Alert tone="danger" className="mb-4">
          {form.formError}
        </Alert>
      )}
      {saved && (
        <Alert tone="success" className="mb-4">
          Produto salvo. A loja mostra a mudança em até 1 minuto.
        </Alert>
      )}
      <fieldset disabled={!editable} className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader title="Informações" />
            <CardBody className="space-y-4">
              <TextField label="Nome" value={form.values.name ?? ''} error={form.errors.name} onChange={(e) => form.set('name', e.target.value)} />
              <Field label="Descrição" error={form.errors.description}>
                {(a) => <Textarea {...a} rows={6} value={form.values.description ?? ''} onChange={(e) => form.set('description', e.target.value)} />}
              </Field>
            </CardBody>
          </Card>

          <ImagesCard product={product} editable={editable} />

          <Card>
            <CardHeader
              title={product.hasVariants ? 'Variantes' : 'Preço e código'}
              description={product.hasVariants ? 'Cada combinação tem preço, código e estoque próprios.' : 'Tem cor, tamanho ou outra opção? Gere as variantes.'}
              actions={editable && <GenerateVariants productId={product.id} defaultPrice={num(product.priceCents)} />}
            />
            <Table>
              <thead>
                <tr>
                  {product.hasVariants && <Th>Variante</Th>}
                  <Th>SKU</Th>
                  <Th>Preço</Th>
                  <Th>Preço "de"</Th>
                  <Th>Custo</Th>
                  <Th>Peso (g)</Th>
                </tr>
              </thead>
              <tbody>
                {variants.map((v, i) => (
                  <Tr key={v.id}>
                    {product.hasVariants && <Td className="whitespace-nowrap font-medium">{v.name}</Td>}
                    <Td>
                      <Input aria-label={`SKU de ${v.name}`} className="w-36" value={v.sku} onChange={(e) => setVariant(i, { sku: e.target.value })} />
                    </Td>
                    <Td>
                      <div className="w-32">
                        <MoneyInput aria-label={`Preço de ${v.name}`} value={v.priceCents} onChange={(c) => setVariant(i, { priceCents: c })} aria-invalid={!!form.errors[`variants.${i}.priceCents`]} />
                      </div>
                    </Td>
                    <Td>
                      <div className="w-32">
                        <MoneyInput aria-label={`Preço "de" de ${v.name}`} value={v.compareAtCents} onChange={(c) => setVariant(i, { compareAtCents: c })} />
                      </div>
                    </Td>
                    <Td>
                      <div className="w-32">
                        <MoneyInput aria-label={`Custo de ${v.name}`} value={v.costCents} onChange={(c) => setVariant(i, { costCents: c })} />
                      </div>
                    </Td>
                    <Td>
                      <Input
                        aria-label={`Peso de ${v.name} em gramas`}
                        inputMode="numeric"
                        className="w-24"
                        value={v.weightGrams ?? ''}
                        onChange={(e) => setVariant(i, { weightGrams: e.target.value ? Number(e.target.value.replace(/\D/g, '')) : null })}
                      />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>

          {fiscal && (
            <Card>
              <CardHeader title="Nota fiscal" description="Confirme estes dados com seu contador." />
              <CardBody className="grid gap-4 sm:grid-cols-2">
                <TextField label="NCM" inputMode="numeric" maxLength={8} value={form.values.ncm ?? ''} error={form.errors.ncm} onChange={(e) => form.set('ncm', e.target.value || null)} />
                <TextField label="CEST" inputMode="numeric" maxLength={7} value={form.values.cest ?? ''} error={form.errors.cest} onChange={(e) => form.set('cest', e.target.value || null)} />
                <Field label="Origem da mercadoria">
                  {(a) => (
                    <Select {...a} value={form.values.origin ?? ''} onChange={(e) => form.set('origin', e.target.value === '' ? null : Number(e.target.value))}>
                      <option value="">Não informada</option>
                      <option value="0">0 – Nacional</option>
                      <option value="1">1 – Estrangeira, importação direta</option>
                      <option value="2">2 – Estrangeira, adquirida no mercado interno</option>
                      <option value="3">3 – Nacional, com mais de 40% de conteúdo importado</option>
                      <option value="5">5 – Nacional, com até 40% de conteúdo importado</option>
                    </Select>
                  )}
                </Field>
                <TextField label="Unidade" maxLength={6} value={form.values.unit ?? ''} onChange={(e) => form.set('unit', e.target.value.toUpperCase())} />
                {product.type === 'SERVICE' && (
                  <TextField label="Código do serviço" value={form.values.serviceCode ?? ''} onChange={(e) => form.set('serviceCode', e.target.value || null)} />
                )}
                <Field label="Perfil tributário" hint="Define CFOP e impostos da nota." className="sm:col-span-2">
                  {(a) => (
                    <Select {...a} value={form.values.taxProfileId ?? ''} onChange={(e) => form.set('taxProfileId', e.target.value || null)}>
                      <option value="">Usar o perfil padrão</option>
                      {(profiles.data ?? []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </CardBody>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="Publicação" />
            <CardBody className="space-y-4">
              <Field label="Situação">
                {(a) => (
                  <Select {...a} value={form.values.status} onChange={(e) => form.set('status', e.target.value as Product['status'])}>
                    <option value="ACTIVE">À venda</option>
                    <option value="DRAFT">Rascunho (não aparece na loja)</option>
                    <option value="ARCHIVED">Arquivado</option>
                  </Select>
                )}
              </Field>
              <Field label="Categoria">
                {(a) => (
                  <Select {...a} value={form.values.categoryId ?? ''} onChange={(e) => form.set('categoryId', e.target.value || null)}>
                    <option value="">Sem categoria</option>
                    {flattenCategories(categories.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Checkbox label="Destaque na página inicial da loja" checked={!!form.values.featured} onChange={(e) => form.set('featured', e.target.checked)} />
              {form.values.type === 'SERVICE' && (
                <ServiceFields
                  values={form.values}
                  errors={form.errors}
                  agenda={isModuleActive('agenda')}
                  checkout={isModuleActive('checkout')}
                  onChange={(patch) => {
                    for (const [k, v] of Object.entries(patch)) form.set(k as keyof ServiceValues, v as never);
                  }}
                />
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Google e redes sociais" />
            <CardBody className="space-y-4">
              <TextField label="Endereço na loja" hint={`/produtos/${form.values.slug ?? ''}`} value={form.values.slug ?? ''} error={form.errors.slug} onChange={(e) => form.set('slug', e.target.value)} />
              <TextField
                label="Título"
                maxLength={70}
                value={form.values.seo?.title ?? ''}
                error={form.errors['seo.title']}
                onChange={(e) => form.set('seo', { ...form.values.seo, title: e.target.value })}
              />
              <Field label="Descrição" error={form.errors['seo.description']} hint="Até 160 caracteres.">
                {(a) => <Textarea {...a} maxLength={160} value={form.values.seo?.description ?? ''} onChange={(e) => form.set('seo', { ...form.values.seo, description: e.target.value })} />}
              </Field>
            </CardBody>
          </Card>
        </div>
      </fieldset>
    </form>
  );
}

function ImagesCard({ product, editable }: { product: Product; editable: boolean }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['product', product.id] });
  const uploader = useUpload(
    (file) => api.post<{ url: string; key: string }>(`/v1/core/products/${product.id}/images/upload-url`, { contentType: file.type, size: file.size }),
    { maxBytes: 10 * 1024 * 1024, accept: (t) => /^image\/(png|jpeg|webp|gif|avif)$/.test(t), typeMessage: 'Envie uma imagem (PNG, JPG, WebP, GIF ou AVIF).' },
  );
  const remove = useMutation({ mutationFn: (imageId: string) => api.delete(`/v1/core/products/${product.id}/images/${imageId}`), onSuccess: refresh });

  async function onFiles(files: File[]) {
    for (const file of files) {
      const key = await uploader.upload(file);
      if (key) await api.post(`/v1/core/products/${product.id}/images`, { key, alt: product.name });
    }
    await refresh();
  }

  return (
    <Card>
      <CardHeader title="Fotos" description="A primeira é a capa. Tamanho máximo de 10 MB por foto." />
      <CardBody className="space-y-4">
        {uploader.error && <Alert tone="danger">{uploader.error}</Alert>}
        {product.images.length > 0 && (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {product.images.map((img) => (
              <li key={img.id} className="group relative">
                <img src={img.urls.w400} onError={(e) => (e.currentTarget.src = img.urls.original)} alt={img.alt ?? ''} className="aspect-square w-full rounded-lg border border-line object-cover" />
                {editable && (
                  <button
                    type="button"
                    className="absolute right-1 top-1 rounded bg-white/90 p-1 text-danger shadow"
                    aria-label="Remover foto"
                    onClick={() => remove.mutate(img.id)}
                  >
                    <Trash2 className="size-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {editable && (
          <FilePicker accept="image/png,image/jpeg,image/webp,image/gif,image/avif" multiple busy={uploader.busy} onFiles={(f) => void onFiles(f)}>
            Arraste as fotos aqui ou
          </FilePicker>
        )}
      </CardBody>
    </Card>
  );
}

/** "cor: Azul, Preto" + "tamanho: P, M, G" → variantes geradas pela API. */
function GenerateVariants({ productId, defaultPrice }: { productId: string; defaultPrice: number | null }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([
    { name: 'Cor', values: '' },
    { name: 'Tamanho', values: '' },
  ]);
  const [price, setPrice] = useState<number | null>(defaultPrice);
  const [error, setError] = useState<string | null>(null);
  const generate = useMutation({
    mutationFn: (body: unknown) => api.post(`/v1/core/products/${productId}/variants/generate`, body),
    onSuccess: async () => {
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['product', productId] });
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const submit = () => {
    const options = Object.fromEntries(
      rows
        .filter((r) => r.name.trim() && r.values.trim())
        .map((r) => [r.name.trim().toLowerCase(), r.values.split(',').map((v) => v.trim()).filter(Boolean)]),
    );
    const parsed = generateVariantsSchema.safeParse({ options, priceCents: price ?? 0 });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Confira as opções.');
      return;
    }
    setError(null);
    generate.mutate(parsed.data);
  };
  const total = rows.reduce((n, r) => n * Math.max(1, r.values.split(',').filter((v) => v.trim()).length), 1);
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Gerar variantes
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Gerar variantes"
        description="Escreva as opções separadas por vírgula. Até 3 tipos de opção."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button loading={generate.isPending} onClick={submit}>
              Gerar {total > 1 ? `${total} variantes` : ''}
            </Button>
          </>
        }
      >
        {/* O diálogo fica dentro do formulário do produto: Enter aqui gera, não salva o produto. */}
        <div
          className="space-y-3"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault();
              submit();
            }
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[8rem_1fr] gap-2">
              <Input aria-label={`Nome da opção ${i + 1}`} value={r.name} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <Input
                aria-label={`Valores da opção ${i + 1}`}
                placeholder="Azul, Preto"
                value={r.values}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, values: e.target.value } : x)))}
              />
            </div>
          ))}
          {rows.length < 3 && (
            <Button size="sm" variant="ghost" onClick={() => setRows([...rows, { name: '', values: '' }])}>
              + Outra opção
            </Button>
          )}
          <Field label="Preço de cada variante">{(a) => <MoneyInput {...a} value={price} onChange={setPrice} />}</Field>
        </div>
      </Dialog>
    </>
  );
}
